import { SECTION_KEYS, type AssetDetail, type PipelineSummary, type SectionKey, type SubjectDraft } from '@lastrouter/contracts';
import {
  Alert,
  ArrowLeft,
  Button,
  Card,
  CardContent,
  CheckCircle2,
  CircleDashed,
  cn,
  Dialog,
  DialogContent,
  Field,
  Input,
  NativeSelect,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  XCircle,
} from '@lastrouter/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ApiError } from '../../api/client.js';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';
import { useSession } from '../../app/session.js';
import { KeyValue, linkClass, LoadingBlock, PageHeader, QueryState, StatusBadge, useUnsavedWarning } from '../../components/common.js';
import { formatDateTime } from '../../lib/format.js';
import { AssetStatusBadge } from '../clients/apps.js';
import { AppQuality } from '../quality.js';
import { AnswerKeyCard } from './answer-key.js';
import { BatteryRun } from './battery-results.js';
import { SubjectEditor } from '../context/subject-form.js';
import { ContextTab } from './context.js';
import { NeedsRun, RisksTab, SummaryTab, useAssetRuns } from './generate.js';
import { ASSET_TABS, assetTabs, partFilled, type AssetTab, type TabState } from './logic.js';
import { CertificationCard } from './pipeline.js';
import { PublishPanel } from './publish.js';
import { ASSET_QUERIES, HistoryPanel, VersionsPanel } from './versions.js';

/*
 * Página do app: o cabeçalho e uma linha de abas, que são as etapas do pipeline de contexto (ADR 0044 e 0049).
 * Os quatro passos que ficavam no topo (gerar, validar, certificar, publicar) viraram os selos das abas.
 */

function NewDraftDialog({ assetId, from, onClose }: { assetId: string; from: { id: string; n: number } | null; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useApiMutation('createVersion', [...ASSET_QUERIES]);
  const [reason, setReason] = useState('');

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate(
      { params: { id: assetId }, body: { from_version_id: from?.id ?? null, reason: reason.trim() } },
      {
        onSuccess: (d) => {
          toast.success(t('assets.draftCreated', { n: d.version.number }));
          onClose();
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('assets.newDraftTitle')} description={from ? t('assets.newDraftFrom', { n: from.n }) : t('assets.newDraftHelp')} closeLabel={t('common.close')}>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Field id="rascunho-motivo" label={t('assets.reason')}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} required />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={create.isPending || !reason.trim()}>
              {t('assets.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DisableDialog({ detail, onClose }: { detail: AssetDetail; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useApiMutation('updateAsset', [...ASSET_QUERIES]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('assets.disable')} description={t('assets.disableConfirm', { name: detail.asset.name })} closeLabel={t('common.close')}>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                { params: { id: detail.asset.id }, body: { status: 'nao_habilitado' } },
                {
                  onSuccess: () => {
                    toast.success(t('assets.disabled'));
                    onClose();
                  },
                  onError: (err) => toast.error(errorMessage(t, err)),
                },
              )
            }
          >
            {t('assets.disable')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TAB_ICON = { done: CheckCircle2, pending: CircleDashed, blocked: XCircle } as const;
const TAB_ICON_TONE = { done: 'text-success', pending: 'text-warning', blocked: 'text-danger' } as const;

function TabIcon({ state }: { state: TabState }) {
  const Icon = TAB_ICON[state];
  return <Icon aria-hidden="true" className={cn('size-4 shrink-0', TAB_ICON_TONE[state])} />;
}

type OnTab = (t: AssetTab) => void;

function AssetView({ detail, tab, onTab }: { detail: AssetDetail; tab: AssetTab | undefined; onTab: OnTab }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isAdmin = Boolean(useSession().data?.is_admin);
  const { asset, published, draft } = detail;
  const base: SubjectDraft | null = draft?.version.subject ?? published?.subject ?? null;
  const runs = useAssetRuns(asset.id);

  const [working, setWorking] = useState<SubjectDraft | null>(base);
  const [reason, setReason] = useState(draft?.version.reason ?? '');
  const [editorKey, setEditorKey] = useState(0);
  const [newDraft, setNewDraft] = useState<{ from: { id: string; n: number } | null } | null>(null);
  const [disabling, setDisabling] = useState(false);

  // Quando o rascunho muda no serviço (salvo, aprovado, aberto, descartado, proposta aceita), a edição recomeça dele.
  // Menos no rascunho que o próprio Salvar abriu: se o salvar falhar depois, o que a pessoa digitou não se perde.
  const keepWorking = useRef(false);
  const baseStamp = draft?.version.updated_at ?? published?.updated_at ?? '';
  useEffect(() => {
    if (keepWorking.current) {
      keepWorking.current = false;
      return;
    }
    setWorking(base);
    setReason(draft?.version.reason ?? '');
    setEditorKey((k) => k + 1);
  }, [baseStamp, draft?.version.id]);

  const dirty = useMemo(() => JSON.stringify(working) !== JSON.stringify(base), [working, base]);
  useUnsavedWarning(dirty);

  // Sem invalidar: o salvar logo em seguida recarrega o app uma vez só.
  const create = useApiMutation('createVersion');
  const save = useApiMutation('saveVersion', [...ASSET_QUERIES]);

  /** Edita direto: sem rascunho aberto, o Salvar abre um a partir da versão publicada e grava nele. */
  async function doSave() {
    if (!working) return;
    try {
      let d = draft;
      if (!d) {
        keepWorking.current = true;
        d = await create.mutateAsync({ params: { id: asset.id }, body: { from_version_id: published?.id ?? null, reason: reason.trim() } });
      }
      await save.mutateAsync({ params: { id: d.version.id }, body: { subject: working, reason: reason.trim(), expected_updated_at: d.version.updated_at } });
      toast.success(t('assets.saved'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && draft) {
        toast.error(t('assets.stale'), {
          duration: Infinity,
          action: { label: t('assets.reload'), onClick: () => void qc.invalidateQueries({ queryKey: ['asset'] }) },
        });
      } else toast.error(errorMessage(t, err));
    }
  }

  function setSection(key: SectionKey, text: string) {
    setWorking((w) => (w ? { ...w, secoes: { ...w.secoes, [key]: text } } : w));
  }

  const run = runs.run;
  const batteryRunning = run?.run.status === 'rodando' && run.run.step === 'bateria';
  const okRun = run?.run.status === 'ok' || batteryRunning ? run : null;
  const { tabs, initial } = assetTabs({
    runStatus: run?.run.status ?? null,
    running: runs.running,
    pipeline: run?.pipeline ?? null,
    criticalFindings: (run?.findings ?? []).filter((f) => f.severity === 'critico').length,
    // Sem medida mestre o app ainda tem contexto: as seções escritas contam (o query monta a medida pelos campos).
    contextFilled: partFilled(base, 'semantica') || SECTION_KEYS.some((k) => partFilled(base, k)),
    draft,
    published: asset.status === 'publicado' && !draft,
  });
  const current = tab ?? initial;
  const label = (k: AssetTab) => t(`assets.tabs.${k}`);
  const stateText = (k: AssetTab) => (tabs[k] ? ` (${t(`assets.tabState.${tabs[k]}`)})` : '');

  const saveBar = (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <Field id="salvar-motivo" label={t('assets.saveReason')}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" onClick={() => void doSave()} disabled={!dirty || save.isPending || create.isPending || !reason.trim()}>
            {t('common.save')}
          </Button>
          {dirty ? <StatusBadge tone="warning">{t('common.unsavedBadge')}</StatusBadge> : null}
          <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t('assets.saveHelp')}</span>
        </div>
      </CardContent>
    </Card>
  );

  const semanticEditor = working ? (
    <SubjectEditor
      key={editorKey}
      subject={working}
      readOnly={false}
      onApply={(s) => {
        // O formulário não mostra as seções: elas seguem como estão na edição local.
        setWorking((w) => ({ ...s, secoes: w?.secoes ?? s.secoes }));
        toast.success(t('context.form.applied'));
      }}
    />
  ) : null;

  const pipelineTab = (render: (p: PipelineSummary) => ReactNode) =>
    okRun?.pipeline ? render(okRun.pipeline) : <NeedsRun runs={runs} onTab={onTab} />;

  return (
    <>
      <PageHeader
        title={asset.name}
        actions={
          <>
            <AssetStatusBadge status={asset.status} />
            {current !== 'context' && base ? <Button onClick={() => onTab('context')}>{t('assets.editContext')}</Button> : null}
            {isAdmin && asset.status !== 'nao_habilitado' ? (
              <Button variant="danger" onClick={() => setDisabling(true)}>
                {t('assets.disable')}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="-mt-3 mb-5 flex flex-col gap-4">
        <KeyValue
          items={[
            { label: t('assets.space'), value: asset.space ?? '-' },
            { label: t('assets.qlikId'), value: <span className="break-all font-mono text-[length:var(--lr-text-sm)]">{asset.external_id}</span> },
            {
              label: t('assets.publishedVersion'),
              value: published ? (
                <span className="flex flex-wrap items-center gap-2">
                  <span>{t('assets.versionN', { n: published.number })}</span>
                  <span className="text-fg-secondary">{formatDateTime(published.published_at)}</span>
                </span>
              ) : (
                '-'
              ),
            },
            { label: t('assets.lastReload'), value: formatDateTime(asset.last_reload_at) },
          ]}
        />
        {asset.missing ? <Alert tone="warning" title={t('assets.missingHelp')} /> : null}
      </div>

      {/* Sem aba na URL, a página abre no próximo passo: espera as gerações para não pular de aba. */}
      {!tab && runs.loading ? (
        <LoadingBlock />
      ) : (
        <Tabs value={current} onValueChange={(v) => onTab(v as AssetTab)}>
          {/* No celular, as abas viram uma lista de escolha: sem rolagem lateral. */}
          <div className="sm:hidden">
            <Field id="app-aba" label={t('assets.tabsLabel')}>
              <NativeSelect value={current} onChange={(e) => onTab(e.target.value as AssetTab)}>
                {ASSET_TABS.map((k) => (
                  <option key={k} value={k}>{`${label(k)}${stateText(k)}`}</option>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <TabsList aria-label={t('assets.tabsLabel')} className="hidden overflow-x-auto sm:flex">
            {ASSET_TABS.map((k) => (
              <TabsTrigger key={k} value={k} className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                {tabs[k] ? <TabIcon state={tabs[k]} /> : null}
                {label(k)}
                {tabs[k] ? <span className="sr-only">({t(`assets.tabState.${tabs[k]}`)})</span> : null}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="summary">
            <SummaryTab runs={runs} next={initial} onTab={onTab} />
          </TabsContent>
          <TabsContent value="risks">
            <RisksTab runs={runs} onTab={onTab} />
          </TabsContent>
          {/* Sempre montada: o que foi digitado no editor da camada semântica e ainda não aplicado sobrevive à troca de aba. */}
          <TabsContent value="context" forceMount className="data-[state=inactive]:hidden">
            <ContextTab
              detail={detail}
              working={working}
              dirty={dirty}
              run={okRun}
              onSection={setSection}
              saveBar={saveBar}
              semanticEditor={semanticEditor}
            />
          </TabsContent>
          <TabsContent value="battery">
            {pipelineTab((p) => (
              // Sub-abas: rodar e acompanhar, o gabarito e a certificação, sem uma página comprida.
              <Tabs defaultValue={(p.battery?.by_status.aprovado ?? 0) > 0 ? 'run' : 'answerKey'}>
                <TabsList aria-label={t('assets.tabs.battery')}>
                  <TabsTrigger value="run">{t('assets.batteryTabs.run')}</TabsTrigger>
                  <TabsTrigger value="answerKey">{t('assets.batteryTabs.answerKey')}</TabsTrigger>
                  <TabsTrigger value="certification">{t('assets.batteryTabs.certification')}</TabsTrigger>
                </TabsList>
                <TabsContent value="run">
                  <BatteryRun run={okRun!} p={p} running={batteryRunning} detail={detail} />
                </TabsContent>
                <TabsContent value="answerKey">
                  <AnswerKeyCard runId={okRun!.run.id} appName={asset.name} appId={asset.external_id} />
                </TabsContent>
                <TabsContent value="certification">
                  <CertificationCard run={okRun!} p={p} />
                </TabsContent>
              </Tabs>
            ))}
          </TabsContent>
          <TabsContent value="publish">
            <PublishPanel detail={detail} dirty={dirty} certification={runs.certification} />
          </TabsContent>
          <TabsContent value="quality">
            <AppQuality subjectId={asset.subject_id} />
          </TabsContent>
          <TabsContent value="versions" className="space-y-6">
            <VersionsPanel detail={detail} onOpenFrom={(id, n) => setNewDraft({ from: { id, n } })} />
            <HistoryPanel assetId={asset.id} />
          </TabsContent>
        </Tabs>
      )}

      {newDraft ? <NewDraftDialog assetId={asset.id} from={newDraft.from} onClose={() => setNewDraft(null)} /> : null}
      {disabling ? <DisableDialog detail={detail} onClose={() => setDisabling(false)} /> : null}
    </>
  );
}

export function AssetPage({ id, tab, onTab }: { id: string; tab: AssetTab | undefined; onTab: OnTab }) {
  const { t } = useTranslation();
  const q = useApiQuery('asset', { params: { id } });
  return (
    <>
      <p className="mb-3">
        {/* Com histórico, volta para a lista como estava (busca, filtros e página ficam na URL). */}
        <Link
          to="/clients"
          className={`${linkClass} inline-flex items-center gap-1`}
          onClick={(e) => {
            if (window.history.length > 1) {
              e.preventDefault();
              window.history.back();
            }
          }}
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('apps.back')}
        </Link>
      </p>
      <QueryState query={q}>{(detail) => <AssetView detail={detail} tab={tab} onTab={onTab} />}</QueryState>
    </>
  );
}
